'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { type AuthViewModeType, AUTH_VIEW_MODE } from '@pixum/auth-ui';

// Set when the user intentionally leaves the auth step. On the next mount the flag is
// consumed to remount AuthFlow — clearing any form errors from the previous visit.
const CART_AUTH_RESET_KEY = 'cart-auth-needs-reset';

interface UseCartAuthNavigationParams {
  initialView: AuthViewModeType;
  overviewHref: string;
  loginHref: string;
  registerHref: string;
  forgotPasswordHref: string;
}

interface UseCartAuthNavigationResult {
  currentView: AuthViewModeType;
  authFlowKey: number;
  loginError: string | null;
  isForgotPasswordSuccess: boolean;
  isNavigatingBack: boolean;
  setLoginError: (error: string | null) => void;
  setIsForgotPasswordSuccess: (value: boolean) => void;
  handleViewChange: (view: AuthViewModeType) => void;
  handleBackToCart: () => void;
}

export function useCartAuthNavigation({
  initialView,
  overviewHref,
  loginHref,
  registerHref,
  forgotPasswordHref
}: UseCartAuthNavigationParams): UseCartAuthNavigationResult {
  const router = useRouter();
  const [currentView, setCurrentView] = useState<AuthViewModeType>(initialView);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isForgotPasswordSuccess, setIsForgotPasswordSuccess] = useState(false);
  // Incremented on browser back/forward to remount AuthFlow with the correct initialView.
  // AuthFlow only reads initialView once (useState internally), so a remount is the only
  // way to reset its internal view state — acceptable here since back/forward is intentional.
  // Also incremented when returning from outside the auth step, to clear stale form errors.
  const [authFlowKey, setAuthFlowKey] = useState(0);

  useEffect(() => {
    // If the user left via "back to cart", consume the flag and reset AuthFlow so
    // stale form errors from the previous visit don't show on return.
    // Also reset isNavigatingBack: if React preserved component state across navigations,
    // the back-navigation loading state would still be true on re-entry.
    setIsNavigatingBack(false);

    if (sessionStorage.getItem(CART_AUTH_RESET_KEY)) {
      sessionStorage.removeItem(CART_AUTH_RESET_KEY);
      setAuthFlowKey(key => key + 1);
    }
  }, []);

  // Sync React state with browser back/forward navigation.
  // Tab switches use window.history.pushState (not router.push) to avoid RSC re-renders,
  // so Next.js won't intercept popstate events — we handle them manually.
  // On each popstate, we map the new pathname to the correct view, clear stale UI state,
  // and bump authFlowKey to remount AuthFlow (the only way to reset its internal view state).
  useEffect(() => {
    function handlePopState() {
      const pathname = window.location.pathname;

      // Strip query params from hrefs before comparing (hrefs may contain '?...')
      let targetView: AuthViewModeType | null = null;

      if (pathname === loginHref.split('?')[0]) {
        targetView = AUTH_VIEW_MODE.LOGIN;
      } else if (pathname === registerHref.split('?')[0]) {
        targetView = AUTH_VIEW_MODE.REGISTER;
      } else if (pathname === forgotPasswordHref.split('?')[0]) {
        targetView = AUTH_VIEW_MODE.FORGOT_PASSWORD;
      }

      if (targetView !== null) {
        setCurrentView(targetView);
        setLoginError(null);
        setIsForgotPasswordSuccess(false);
        setAuthFlowKey(key => key + 1);
      }
    }

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [loginHref, registerHref, forgotPasswordHref]);

  const handleBackToCart = useCallback(() => {
    sessionStorage.setItem(CART_AUTH_RESET_KEY, '1');
    setIsNavigatingBack(true);
    router.replace(overviewHref);
  }, [router, overviewHref]);

  function handleViewChange(view: AuthViewModeType): void {
    setCurrentView(view);
    setLoginError(null);
    setIsForgotPasswordSuccess(false);

    // Use pushState instead of router.push to update the URL without triggering
    // a server re-render — router.push causes the RSC to re-run with a new
    // initialView, which changes the key on CartAuthStepClient and remounts it,
    // losing the active-tab state and causing a visible re-render flash.
    switch (view) {
      case AUTH_VIEW_MODE.LOGIN:
        window.history.pushState(null, '', loginHref);
        break;
      case AUTH_VIEW_MODE.REGISTER:
        window.history.pushState(null, '', registerHref);
        break;
      case AUTH_VIEW_MODE.FORGOT_PASSWORD:
        window.history.pushState(null, '', forgotPasswordHref);
        break;
      default:
        break;
    }
  }

  return {
    currentView,
    authFlowKey,
    loginError,
    isForgotPasswordSuccess,
    isNavigatingBack,
    setLoginError,
    setIsForgotPasswordSuccess,
    handleViewChange,
    handleBackToCart
  };
}
